/**
 * Team key synchronization — fetch and unwrap team keys after login/restore.
 *
 * Called after the user has authenticated and restored their E2EE key material.
 * Fetches all teams, downloads the wrapped keys, and unwraps them using the
 * user's X25519 private key. Stores unwrapped team AES keys in encryptionStore
 * (in-memory only — never persisted to disk), with one exception: the PERSONAL
 * team's key is also the device key, and `persistPersonalTeamKey` is how it
 * reaches the secure store. See docs/encryption.md, "User-Scoped Key Naming".
 */

import { teamsApi } from "../api/client";
import { useEncryptionStore } from "../stores/encryptionStore";
import { useTeamStore } from "../stores/teamStore";
import { unwrapTeamKey, deriveX25519KeyPair } from "./teamCrypto";
import { base64ToUint8Array } from "./encoding";
import { getSeed, importAndStoreRawKey } from "./crypto";
import { captureException } from "../observability/sentry";

interface WrappedKey {
  key_version: number;
  wrapped_key: string;
}

/** A wrapped key as `/encryption/verify-phrase` returns it — tagged with its team. */
export interface WrappedTeamKey extends WrappedKey {
  team_id: string;
}

/**
 * Pick the current key epoch. A team accumulates one wrapped key per rotation,
 * and only the highest version decrypts today's content — the server returns
 * them in ascending order but we select explicitly rather than trusting that.
 */
function latestKey(keys: WrappedKey[]): WrappedKey | null {
  if (keys.length === 0) return null;
  return keys.reduce((a, b) => (b.key_version > a.key_version ? b : a));
}

/**
 * Sync all team keys for the current user.
 *
 * Swallows its own errors — one team whose key will not open must not block the
 * rest — so a caller that needs to know whether the PERSONAL key arrived checks
 * `persistPersonalTeamKey`'s return value rather than trusting this resolved.
 *
 * @param x25519PrivateKey - The user's X25519 private key (derived from PBKDF2 seed)
 * @param wrappedKeys - Wrapped keys already in hand, as `/encryption/verify-phrase`
 *   returns them. When supplied, the per-team `GET /teams/{id}/keys` is skipped;
 *   `GET /teams/my-teams` still runs because that is where `team_type` lives.
 */
export async function syncTeamKeys(
  x25519PrivateKey: Uint8Array,
  wrappedKeys?: WrappedTeamKey[]
): Promise<void> {
  try {
    // Fetch all teams the user belongs to
    const teamsResponse = await teamsApi.getMyTeams();
    const teams = teamsResponse.data;

    // Update teamStore with fetched teams
    useTeamStore.setState({ teams });

    const encStore = useEncryptionStore.getState();

    for (const team of teams) {
      try {
        // Which team is the personal one is a fact about the team, not about
        // whether its key opens — record it before the unwrap can throw, so a
        // wrapped key that fails still leaves `getKeyForTeam` able to route
        // the personal team to the persisted device key.
        if (team.team_type === "personal") {
          encStore.setPersonalTeamId(team.id);
        }

        const keys: WrappedKey[] = wrappedKeys
          ? wrappedKeys.filter((k) => k.team_id === team.id)
          : (await teamsApi.getTeamKeys(team.id)).data.keys;

        // Unwrap the current key epoch
        const current = latestKey(keys);
        if (current) {
          const wrappedBytes = base64ToUint8Array(current.wrapped_key);
          const unwrappedKey = unwrapTeamKey(wrappedBytes, x25519PrivateKey);
          encStore.setTeamKey(team.id, unwrappedKey);
        }
      } catch (error) {
        console.error(`Failed to sync keys for team ${team.id}:`, error);
        captureException(error, { context: "syncTeamKeys.team", teamId: team.id });
      }
    }

    // Set default active team to personal team if not set
    const { activeTeamId } = useTeamStore.getState();
    if (!activeTeamId) {
      const personalTeam = teams.find((t: { team_type: string }) => t.team_type === "personal");
      if (personalTeam) {
        useTeamStore.getState().setActiveTeam(personalTeam.id);
      }
    }
  } catch (error) {
    console.error("Failed to sync team keys:", error);
    captureException(error, { context: "syncTeamKeys" });
  }
}

/**
 * Copy the personal team's key from memory into the device-key slot.
 *
 * ⚠️  THIS IS WHAT MAKES A DEVICE USABLE, and every provisioning path must call
 *     it. The seed alone is not enough: `getKeyForTeam` resolves the personal
 *     team to the persisted device key, and a device holding only a seed has
 *     nothing there. QR transfer used to stop at `storeSeed` and report success;
 *     the next launch found no key and bounced to the restore screen.
 *
 * Returns false when there is nothing to persist — `syncTeamKeys` did not run,
 * could not reach the server, or the personal team's wrapped key did not open.
 * Callers must treat false as a failed provisioning, never as a success.
 */
export async function persistPersonalTeamKey(userId: string): Promise<boolean> {
  const encStore = useEncryptionStore.getState();
  const { personalTeamId } = encStore;
  if (!personalTeamId) return false;

  const key = encStore.getTeamKey(personalTeamId);
  if (!(key instanceof Uint8Array)) return false;

  await importAndStoreRawKey(key, userId);
  return true;
}

/**
 * Lazy-sync a single team's key on demand.
 *
 * Called by getKeyForTeam() when a shared team key is missing from memory.
 * Re-derives the X25519 private key from the persisted PBKDF2 seed, fetches
 * the wrapped key for the specified team, unwraps it, and stores it.
 *
 * @returns The unwrapped team AES key, or null if sync failed.
 */
export async function lazySyncTeamKey(teamId: string): Promise<unknown | null> {
  const encStore = useEncryptionStore.getState();
  const userId = encStore.currentUserId;
  if (!userId) return null;

  try {
    // Load persisted PBKDF2 seed from device storage
    const seed = await getSeed(userId);
    if (!seed) return null;

    // Derive X25519 private key from seed
    const { privateKey } = deriveX25519KeyPair(seed);

    // Fetch wrapped keys for just this team
    const keysRes = await teamsApi.getTeamKeys(teamId);
    const { keys } = keysRes.data;

    const current = latestKey(keys);
    if (!current) return null;

    const wrappedBytes = base64ToUint8Array(current.wrapped_key);
    encStore.setTeamKey(teamId, unwrapTeamKey(wrappedBytes, privateKey));

    return encStore.getTeamKey(teamId);
  } catch (error) {
    console.error(`Lazy sync failed for team ${teamId}:`, error);
    captureException(error, { context: "lazySyncTeamKey", teamId });
    return null;
  }
}
